import {ComponentType, PropsWithChildren, Suspense} from 'react'

import {ModalDataMap} from '../interfaces'

import {ModalContext} from './context'
import {useModalStore} from './useModalStore'
import usePortal from './usePortal'

/**
 * 모달 상태를 제어하는 Provider
 * @param children
 * @param modalRegistry = 지역에서 관리하는 modal 목록
 * useModalRegistry 로도 동일한 기능 지원
 */
export function ModalProvider({children, modalRegistry}: PropsWithChildren<{modalRegistry?: ModalDataMap}>) {
    const {openedModals, modal} = useModalStore({modalRegistry})
    const {Portal} = usePortal()

    return (
        <ModalContext.Provider value={modal}>
            {children}
            <Portal>
                <Suspense fallback={null}>
                    {openedModals.map(({key, component: ModalComponent, data}) => (
                        <ModalComponent key={key} {...data} />
                    ))}
                </Suspense>
            </Portal>
        </ModalContext.Provider>
    )
}

/**
 * 모달 레지스트리를 미리 정의하여 ModalProvider 를 생성하는 HOC
 * - 동일한 모달 설정을 여러 곳에서 재사용할 때 유용합니다.
 * @param modalRegistry - 사전 정의된 modal 목록
 * @returns ModalProvider로 감싸진 컴포넌트를 반환하는 함수
 */
export const createModalContainer =
    (modalRegistry: ModalDataMap) =>
    ({children}: PropsWithChildren) => {
        return <ModalProvider modalRegistry={modalRegistry}>{children}</ModalProvider>
    }

/**
 * 컴포넌트를 ModalProvider 로 감싸는 HOC
 * - 내부에서 hook 을 바로 써야하는데 Component 를 하나 추가하는 것보다 감싸서 관리할때 유용합니다.
 */
export function withModalProvider<P extends object>(Component: ComponentType<P>, modalRegistry?: ModalDataMap) {
    return (props: P) => {
        return (
            <ModalProvider modalRegistry={modalRegistry}>
                <Component {...props} />
            </ModalProvider>
        )
    }
}
